import React, { useState } from "react";
import { Button, Modal, Box, SpaceBetween, RadioGroup, Header } from "@amzn/awsui-components-react";
import { getDocumentFromVault } from 'src/Ateam-BI/Vault/VaultAPIs';
import { VAULT_SERVICES } from 'src/Ateam-BI/Vault/constants';

const DownloadTieOutResultsButton = ({ data }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const hasPdf = !!data?.annotated_pdf_s3_path;
  const hasJson = !!data?.response_s3_path;
  const defaultFormat = hasPdf ? "pdf" : "json";

  const [downloadFormat, setDownloadFormat] = useState(defaultFormat);

  const handleDownloadClick = () => {
    setShowModal(true);
  };

  const handleDismiss = () => {
    setShowModal(false);
    setDownloadFormat(defaultFormat);
  };

  const handleConfirmDownload = async () => {
    setShowModal(false);
    setIsDownloading(true);

    try {
      let s3Path;
      let fileName;

      if (downloadFormat === "pdf") {
        s3Path = data.annotated_pdf_s3_path;
        fileName = `${data.section || 'tie-out-results'}_annotated.pdf`;
      } else {
        s3Path = data.response_s3_path;
        fileName = `${data.section || 'tie-out-results'}.json`;
      }

      if (!s3Path) {
        console.error(`No S3 path available for ${downloadFormat} download`);
        return;
      }

      // Parse s3:// URI to extract file path
      const withoutProtocol = s3Path.replace(/^s3:\/\//, '');
      const firstSlashIndex = withoutProtocol.indexOf('/');
      const filePath = withoutProtocol.substring(firstSlashIndex + 1);

      // Get presigned URL from Vault
      const contentType = downloadFormat === "pdf" ? "pdf" : "json";
      const document = await getDocumentFromVault(contentType, VAULT_SERVICES.DOCEVAL, { "file_path": filePath });
      const fileUrl = document?.fileUrl;

      if (!fileUrl) {
        console.error(`Failed to get presigned URL for ${downloadFormat} file`);
        return;
      }

      // Download the file
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to download ${downloadFormat}: ${response.status}`);
      }
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${downloadFormat} file:`, error);
    } finally {
      setIsDownloading(false);
      setDownloadFormat(defaultFormat);
    }
  };

  const radioOptions = [
    ...(hasPdf ? [{ value: "pdf", label: "PDF", description: "Download the annotated PDF document" }] : []),
    ...(hasJson ? [{ value: "json", label: "JSON", description: "Download the raw JSON results" }] : []),
  ];

  return (
    <>
      <Button
        iconName="download"
        variant="icon"
        onClick={handleDownloadClick}
        disabled={isDownloading || (!hasPdf && !hasJson)}
        loading={isDownloading}
      />
      <Modal
        visible={showModal}
        onDismiss={handleDismiss}
        header={<Header variant="h2">Download Tie-Out Results</Header>}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDismiss}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirmDownload} disabled={!downloadFormat}>
                Download
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Box>Select the format you would like to download:</Box>
          <RadioGroup
            onChange={({ detail }) => setDownloadFormat(detail.value)}
            value={downloadFormat}
            items={radioOptions}
          />
        </SpaceBetween>
      </Modal>
    </>
  );
};

export default DownloadTieOutResultsButton;
